import { useState } from 'react';
import { MailX, Loader2, Play } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCommsPause, useSetCommsPause } from '@/hooks/admin/useCommsPause';

function when(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Red strip across every admin page while communications are paused. A pause
 * nobody can see is how it gets left on, so it shows everywhere, not only on
 * the card that set it.
 */
export function CommsPauseBanner() {
  const { data } = useCommsPause();
  const set = useSetCommsPause();
  if (!data?.paused) return null;
  return (
    <div className="bg-red-600 text-white text-sm px-6 py-2 flex flex-wrap items-center gap-x-4 gap-y-1">
      <MailX size={16} aria-hidden />
      <span className="font-semibold">Customer emails are PAUSED</span>
      <span className="opacity-90">
        {data.reason ? `${data.reason} · ` : ''}
        {data.by ? `by ${data.by} ` : ''}
        {data.at ? `on ${when(data.at)}` : ''}
        {data.held_24h > 0 ? ` · ${data.held_24h} held in the last 24h` : ''}
      </span>
      {data.env_paused ? (
        <span className="opacity-90">Set in Netlify (COMMS_PAUSED) — remove it there to resume.</span>
      ) : (
        <button
          type="button"
          className="ml-auto underline font-semibold disabled:opacity-60"
          disabled={set.isPending}
          onClick={() => {
            if (window.confirm('Resume customer emails now?')) set.mutate({ paused: false });
          }}
        >
          Resume
        </button>
      )}
    </div>
  );
}

/** The switch itself, at the top of Company. */
export function CommsPauseCard() {
  const { data, isLoading, error } = useCommsPause();
  const set = useSetCommsPause();
  const [reason, setReason] = useState('');

  if (isLoading) return null;
  if (error || !data) {
    return (
      <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800">
        Could not read the communications switch: {error instanceof Error ? error.message : 'unknown error'}.
        The emergency stop still works from Netlify: set <code>COMMS_PAUSED=1</code> and redeploy.
      </div>
    );
  }

  const paused = data.paused;
  return (
    <div
      className={
        paused
          ? 'rounded-lg border-2 border-red-500 bg-red-50 p-4 space-y-3'
          : 'rounded-lg border border-brix-border bg-white p-4 space-y-3'
      }
    >
      <div className="flex items-start gap-3">
        <MailX size={20} className={paused ? 'text-red-600' : 'text-brix-muted'} aria-hidden />
        <div className="flex-1">
          <h2 className="font-semibold text-brix-ink">
            Communications kill switch {paused ? '— PAUSED' : ''}
          </h2>
          <p className="text-sm text-brix-muted mt-0.5">
            Stops every email to customers: invoices, statements, reminders, order updates,
            receipts, remittance and credit notices. <strong>Not</strong> stopped: sign-in and
            verification codes, and anything addressed only to our own staff. Orders and payments
            still work. Held emails are not queued — they are logged as “held” and do not send
            later, so after resuming, anything that still matters is re-sent by hand.
          </p>
        </div>
      </div>

      {paused ? (
        <div className="space-y-2">
          <p className="text-sm text-red-800">
            Paused{data.by ? ` by ${data.by}` : ''}
            {data.at ? ` on ${when(data.at)}` : ''}
            {data.reason ? ` — “${data.reason}”` : ''}. {data.held_24h} held in the last 24h
            {data.last_held_at ? ` (latest ${when(data.last_held_at)})` : ''}.
          </p>
          {data.env_paused ? (
            <p className="text-sm font-semibold text-red-800">
              COMMS_PAUSED is set in Netlify, so this page cannot lift it. Remove the variable and
              redeploy to resume.
            </p>
          ) : (
            <Button
              disabled={set.isPending}
              onClick={() => {
                if (window.confirm('Resume customer emails now?')) set.mutate({ paused: false });
              }}
            >
              {set.isPending ? <Loader2 className="animate-spin" size={14} /> : <Play size={14} />}
              <span className="ml-1.5">Resume communications</span>
            </Button>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Input
            className="max-w-xs"
            placeholder="Reason (optional, shown on the banner)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <Button
            className="bg-red-600 hover:bg-red-700 text-white"
            disabled={set.isPending}
            onClick={() => {
              if (
                window.confirm(
                  'Pause ALL customer emails now? Nothing is queued — held emails will not send after you resume.',
                )
              ) {
                set.mutate({ paused: true, reason }, { onSuccess: () => setReason('') });
              }
            }}
          >
            {set.isPending ? <Loader2 className="animate-spin" size={14} /> : <MailX size={14} />}
            <span className="ml-1.5">Pause all customer emails</span>
          </Button>
        </div>
      )}
      {set.isError && (
        <p className="text-sm text-red-700">{set.error instanceof Error ? set.error.message : 'Failed'}</p>
      )}
    </div>
  );
}
